import "@/utils/error-suppression";
import { LanguageProvider } from "@/components/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LoginForm } from "@/components/LoginForm";
import { Layout } from "@/components/Layout";
import { Dashboard } from "@/components/Dashboard";
import { Portfolio } from "@/components/Portfolio";
import { Deals } from "@/components/Deals";
import { Aportaciones } from "@/components/Aportaciones";
import { Reportes } from "@/components/Reportes";
import { Profile } from "@/components/Profile";
import { Academy } from "@/components/Academy";
import { Fees } from "@/components/Fees";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AdminDashboardView } from "@/components/admin/AdminDashboardView";
import { AdminInversores } from "@/components/admin/AdminInversores";
import { AdminActivos } from "@/components/admin/AdminActivos";
import { AdminReportes } from "@/components/admin/AdminReportes";
import { AdminConfiguracion } from "@/components/admin/AdminConfiguracion";
import { AdminRendimiento } from "@/components/admin/AdminRendimiento";
import FeesManager from "@/components/admin/FeesManager";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { login, logout } from "@/store/slices/authSlice";
import {
  setInvestorPage,
  setAdminPage,
  resetNavigation,
} from "@/store/slices/navigationSlice";
import type { User } from "@/store/slices/authSlice";
import type { InvestorPage, AdminPage } from "@/store/slices/navigationSlice";
import { DatabaseProvider, useDatabase } from "./components/DatabaseProvider";
import { LoaderOverlay } from "@/components/ui/loader";
import { useEffect } from "react";

export default function App() {
  return (
    <DatabaseProvider>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </DatabaseProvider>
  );
}

function AppContent() {
  const dispatch = useAppDispatch();
  const { isAuthenticated, userType } = useAppSelector((state) => state.auth);
  const { currentInvestorPage, currentAdminPage } = useAppSelector(
    (state) => state.navigation
  );
  const { user } = useDatabase(); // provider

  useEffect(() => {
    // cuando provider cambia, mantén redux en sync
    if (user.isAuthenticated && user.user) {
      const mapped: User = {
        id: user.user.id,
        email: user.user.email,
        name: `${user.user.first_name ?? ""} ${
          user.user.last_name ?? ""
        }`.trim(),
        type: user.user.user_type === "admin" ? "admin" : "investor",
      };
      dispatch(login({ userType: mapped.type, user: mapped }));
    } else {
      dispatch(logout());
      dispatch(resetNavigation());
    }
  }, [user.isAuthenticated, user.user]); // eslint-disable-line

  if (user.loading) {
    return <LoaderOverlay label="Cargando tu panel…" />;
  }

  const handleLoginSuccess = (user: User) => {
    dispatch(login({ userType: user.type, user }));
  };

  const handleLogout = () => {
    user.signOut();
  };

  const handleInvestorPageChange = (page: InvestorPage) => {
    dispatch(setInvestorPage(page));
  };

  const handleAdminPageChange = (page: string) => {
    dispatch(setAdminPage(page as AdminPage));
  };

  const renderInvestorPage = () => {
    switch (currentInvestorPage) {
      case "dashboard":
        return (
          <Dashboard
            userType="investor"
            onPageChange={handleInvestorPageChange}
          />
        );
      case "fees":
        return <Fees />;
      case "mi-inversion":
        return <Aportaciones />;
      case "portfolio":
        return <Portfolio />;
      case "pro-ratas":
        return <Deals />;
      case "reportes":
      case "reportes-financiero":
      case "reportes-operativo":
      case "reportes-documentacion":
        return <Reportes currentPage={currentInvestorPage} />;
      case "profile":
        return <Profile userType="investor" />;
      case "academy":
        return <Academy />;
      default:
        return (
          <Dashboard
            userType="investor"
            onPageChange={handleInvestorPageChange}
          />
        );
    }
  };

  const renderAdminPage = () => {
    switch (currentAdminPage) {
      case "dashboard":
        return <AdminDashboardView />;
      case "activos":
        return <AdminActivos />;
      case "inversores":
        return <AdminInversores />;
      case "fees":
        return <FeesManager />;
      case "operaciones":
        return <AdminDashboardView />;
      case "reportes":
        return <AdminReportes />;
      case "configuracion":
        return <AdminConfiguracion />;
      case "rendimiento":
        return <AdminRendimiento />;
      default:
        return <AdminDashboardView />;
    }
  };

  // If user is authenticated, show the appropriate application view
  if (isAuthenticated && userType === "investor") {
    return (
      <Layout
        currentPage={currentInvestorPage}
        userType="investor"
        onPageChange={handleInvestorPageChange}
        onLogout={handleLogout}
      >
        {renderInvestorPage()}
      </Layout>
    );
  }

  if (isAuthenticated && userType === "admin") {
    return (
      <AdminLayout
        currentPage={currentAdminPage}
        onPageChange={handleAdminPageChange}
        onLogout={handleLogout}
      >
        {renderAdminPage()}
      </AdminLayout>
    );
  }

  // Show login page
  if (!user.isAuthenticated) {
    return (
      <div className="min-h-screen flex relative">
        {/* Language Selector - Fixed Position */}
        <div className="fixed top-6 right-6 z-50">
          <LanguageSelector />
        </div>

        {/* Left Side - Image Only */}
        <div className="hidden lg:flex lg:w-[40%] relative overflow-hidden">
          <img
            src="/images/login_img.png"
            alt="Bellamy & Partners"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/20 to-primary/10">
            {/* Brand Content */}
            <div className="absolute inset-0 flex flex-col justify-center items-center text-white p-8">
              <div className="text-center space-y-5 max-w-lg">
                <h1 className="text-6xl font-medium tracking-tight">
                  Bellamy & Partners
                </h1>
                <p className="text-2xl text-white/90 leading-relaxed">
                  Providing Value To Society Through Profitable Assets
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-[60%] flex items-center justify-center p-8 lg:p-12 bg-gray-50 overflow-y-auto min-h-screen">
          <div className="w-full max-w-xl my-auto py-8">
            <LoginForm onLoginSuccess={handleLoginSuccess} />
          </div>
        </div>
      </div>
    );
  } else {
    return null;
  }
}
